import { API } from "@/api";
import { EthereumProvider } from "@walletconnect/ethereum-provider";

import detectEthereumProvider from "@metamask/detect-provider";
import Web3 from "web3";
import { getReferralData } from "@/helpers";
import { logoutSession } from "@/helpers/sessionLogout";
import { trackWalletProvider, withWalletSigning } from "@/helpers/walletSigning";
import {
  applyReferralProcessingResult,
  hasReferralPayload,
  submitStoredReferral,
} from "@/helpers/referralAttribution";
import { RESET_PROJECTS, SET_LEADERBOARD_DATA, SET_IPO_LEADERBOARD_DATA, SET_USER_STAKES } from "@/store/mutation-types";
import ATD_TOKEN_ABI from "@/util/constants/contractsABI/atdToken.json";
import etherium from "@/util/constants/networks/etherium.json";
import binance from "@/util/constants/networks/binance.json";

import STAKING_ABI from "@/util/constants/contractsABI/staking.json";
// const  ATD_ADDRESS = process.env.VUE_APP_MAIN_TOKEN_CONTRACT_ADDRESS;

const providers = {
  metaMask: "metamask",
  walletConnect: "walletConnect",
};
let restoringSession = null;

const actions = {
  async restoreSession({ state, dispatch }) {
    if (!state.accessToken) return;
    const expiration = Number(state.tokenExpiration);
    if (!Number.isFinite(expiration) || expiration <= Date.now()) {
      await dispatch('logOut', { revoke: false });
      return;
    }
    if (restoringSession) return restoringSession;
    const token = state.accessToken;
    restoringSession = (async () => {
      try {
        if (!state.provider) await dispatch('initWeb3');
        if (state.accessToken !== token) return;
        const accounts = await state.web3.eth.getAccounts();
        if (state.accessToken !== token) return;
        if (!accounts.length) {
          await dispatch('logOut', { revoke: false });
          return;
        }
        if (!state.userProfile.userERCAddress) await dispatch('fetchUserProfile');
        if (state.accessToken !== token || !state.userProfile.userERCAddress) return;
        if (accounts[0].toLowerCase() !== state.userProfile.userERCAddress.toLowerCase()) {
          await dispatch('logOut', { revoke: false });
          return;
        }
        if (!state.tokenContract) await dispatch('setTokenContract');
      } catch (error) {
        if (state.accessToken === token && (error.code === 'WALLET_SESSION_MISSING' || error.code === 4100)) {
          await dispatch('logOut', { revoke: false });
        } else {
          // Network/RPC errors are retried on the next check without losing auth.
          console.error(error);
        }
      } finally {
        restoringSession = null;
      }
    })();
    return restoringSession;
  },
  async refreshToken({ commit, dispatch }) {
    const token = localStorage.getItem('access_token');
    const tokenResponse = await API.post("/auth/refresh");

    if (tokenResponse?.data?.access_token && localStorage.getItem('access_token') === token) {
      //saveToStore('access_token', tokenResponse?.data?.access_token);
      commit("SET_ACCESS_TOKEN", tokenResponse.data.access_token);
      commit("SET_TOKEN_EXPIRATION", Date.now() + tokenResponse.data.expires_in * 1000);
      await dispatch("fetchUserProfile");
    }
  },
  async connectWallet({ commit, dispatch, state }) {
    return withWalletSigning(async () => {
      await dispatch("determineWeb3Provider");

      const { data } = await API.get("/auth/signature");
      let web3 = state.web3;
      const referralPayload = getReferralData();
      await state.provider.request({ method: 'eth_requestAccounts' });
      const address = (await web3.eth.getAccounts())[0];
      const signature = await web3.eth.personal.sign(data.message, address);
      let requestPayload = {
        nonce: data.code,
        address,
        signature,
      };

      if (hasReferralPayload(referralPayload)) {
        requestPayload.referral_code = referralPayload.referral_code;
      }

      const tokenResponse = await API.post("/auth/authenticate", requestPayload);

      if (hasReferralPayload(referralPayload)) {
        applyReferralProcessingResult(tokenResponse && tokenResponse.data);
      }

      if (tokenResponse?.data?.access_token) {
        //saveToStore('access_token', tokenResponse?.data?.access_token);
        commit("SET_ACCESS_TOKEN", tokenResponse.data.access_token);
        commit("SET_TOKEN_EXPIRATION", Date.now() + tokenResponse.data.expires_in * 1000);
        await dispatch("setTokenContract");
        await dispatch("fetchUserProfile");
        await dispatch("attachStoredReferral");
      }
    });
  },
  async connectWalletConnect({ commit, dispatch, state }) {
    return withWalletSigning(async () => {
      await dispatch("determineWeb3ProviderWalletConnect");

      const { data } = await API.get("/auth/signature");
      const referralPayload = getReferralData();
      const web3 = state.web3;
      const address = (await web3.eth.getAccounts())[0];
      const signature = await web3.eth.personal.sign(data.message, address);
      const requestPayload = {
        nonce: data.code,
        address,
        signature,
        providerName: 'walletconnect'
      };

      if (hasReferralPayload(referralPayload)) {
        requestPayload.referral_code = referralPayload.referral_code;
      }

      const tokenResponse = await API.post("/auth/authenticate", requestPayload);

      if (hasReferralPayload(referralPayload)) {
        applyReferralProcessingResult(tokenResponse && tokenResponse.data);
      }

      if (tokenResponse?.data?.access_token) {
        commit("SET_ACCESS_TOKEN", tokenResponse.data.access_token);
        commit("SET_TOKEN_EXPIRATION", Date.now() + tokenResponse.data.expires_in * 1000);
        await dispatch("setTokenContract");
        await dispatch("fetchUserProfile");
        await dispatch("attachStoredReferral");
      }
    });
  },
  async logOut({ commit, state }, { revoke = true } = {}) {
    const token = localStorage.getItem("access_token");
    const provider = state.provider || state.walletConnectProvider;
    const logout = logoutSession({
      token,
      revoke,
      post: (url, payload, config) => API.post(url, payload, config),
      clear: () => {
        commit("SET_ACCESS_TOKEN", '');
        commit("SET_TOKEN_EXPIRATION", '');
        ['access_token', 'token_expiration', 'provider'].forEach(key => localStorage.removeItem(key));
        commit("RESET_STATE");
      },
    });
    if (provider?.isWalletConnect) {
      // The SDK removes its own persisted session; unrelated site data survives.
      Promise.resolve().then(() => provider.disconnect()).catch(console.error);
    }
    return logout;
  },
  async attachStoredReferral() {
    try {
      await submitStoredReferral({
        post: (url, payload) => API.post(url, payload),
        isAuthenticated: Boolean(localStorage.getItem("access_token")),
      });
    } catch (error) {
      console.error(error);
    }
  },
  async fetchUserProfile({ state, commit }) {
    const token = state.accessToken;
    try {
      const response = await API.get("/user-profile");
      if (state.accessToken === token) commit("SET_USER_PROFILE", response.data);
    } catch (error) {
      // The API interceptor handles 401. Network and server errors preserve auth.
      console.error(error);
    }
  },
  async resetProjects({ commit }) {
    commit(RESET_PROJECTS);
  },
  async initWeb3({ commit, state }) {
    const token = state.accessToken;
    let provider;
    let providerName = localStorage.getItem("provider");

    if (providerName === providers.walletConnect) {
      provider = await EthereumProvider.init({
        projectId: process.env.VUE_APP_PROJECT_ID,
        chains: [56],
        showQrModal: true,
        optionalMethods: [
          "personal_sign",
          "eth_signTypedData",
          "eth_signTypedData_v4",
          "eth_sign",
        ],
      });
      // A logout while the SDK initializes must not restore a stale provider.
      if (state.accessToken !== token) return;
      commit('WALLETCONNECT_PROVIDER', provider);
      if (!provider.session) {
        const error = new Error('Saved WalletConnect session is unavailable');
        error.code = 'WALLET_SESSION_MISSING';
        throw error;
      }
    } else if (providerName === providers.metaMask) {
      provider = Web3.givenProvider;
    }

    if (!provider) {
      const error = new Error('Saved wallet is unavailable');
      error.code = 'WALLET_SESSION_MISSING';
      throw error;
    }
    const web3 = new Web3(trackWalletProvider(provider));
    commit("SET_WEB3", {
      web3: web3,
      providerName,
      provider,
    });

    const blockchainInfoWeb3 = new Web3(process.env.VUE_APP_NETWORK_URL);
    commit("SET_BLOCKCHAIN_INFO_WEB3", {
      blockchainInfoWeb3,
    });
  },
  async determineWeb3Provider({ commit }) {
    try {
      // const provider = await web3Modal.connect();
      const provider = await detectEthereumProvider();
      if (!provider) throw new Error('MetaMask is unavailable');

      let providerName = providers.metaMask;

      if (provider.isWalletConnect) {
        providerName = providers.walletConnect;
      }

      localStorage.setItem("provider", providerName);
      commit("SET_WEB3", {
        web3: new Web3(trackWalletProvider(provider)),
        providerName,
        provider,
      });

      const blockchainInfoWeb3 = new Web3(process.env.VUE_APP_NETWORK_URL);
      commit("SET_BLOCKCHAIN_INFO_WEB3", {
        blockchainInfoWeb3,
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async determineWeb3ProviderWalletConnect({ commit }) {
    try {
      const provider = await EthereumProvider.init({
        projectId: process.env.VUE_APP_PROJECT_ID,
        chains: [56],
        showQrModal: true,
        optionalMethods: [
          "personal_sign",
          "eth_signTypedData",
          "eth_signTypedData_v4",
          "eth_sign",
        ],
      });
      commit("WALLETCONNECT_PROVIDER", provider);
      await provider.connect();
      let providerName = providers.metaMask;
      if (provider.isWalletConnect) {
        providerName = providers.walletConnect;
      }

      localStorage.setItem("provider", providerName);
      commit("SET_WEB3", {
        web3: new Web3(trackWalletProvider(provider)),
        providerName,
        provider,
      });

      const blockchainInfoWeb3 = new Web3(process.env.VUE_APP_NETWORK_URL);
      commit("SET_BLOCKCHAIN_INFO_WEB3", {
        blockchainInfoWeb3,
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async setTokenContract({ state, commit }) {
    commit(
      "SET_TOKEN_CONTRACT",
      new state.web3.eth.Contract(
        state.CPLTContractAbi,
        state.CPLTContractAddress
      )
    );
    commit(
      "SET_TOKEN_CONTRACT_INFO",
      new state.blockchainInfoWeb3.eth.Contract(
        state.CPLTContractAbi,
        state.CPLTContractAddress
      )
    );
    commit(
      "SET_OLD_TOKEN_CONTRACT_INFO",
      new state.blockchainInfoWeb3.eth.Contract(
        state.ATDContractAbi,
        state.ATDContractAddress
      )
    );
  },
  async fetchProjectContractInfo({ state }, project) {
    try {
      const web3 = state.blockchainInfoWeb3;
      const currentAccount = state.userProfile.userERCAddress;
      const {
        contractAbi,
        contractAddress,
        isIDO,
        isOTC,
        isOTCOnMarket,
        isIDOAdvanced,
        poolId,
        tokenPrice,
        isTrueYieldStaking,
      } = project;

      let currentContract = new web3.eth.Contract(
        JSON.parse(contractAbi),
        contractAddress
      );

      let tokenContract = state.tokenContractInfo;
      const info = {
        totalStaked: project.totalInvestments,
        yourStake: "--",
        earned: "--",
        atdBalance: 0,
        userParticipated: false,
        idoTokenAddress: "",
        refunded: "--",
      };

      let poolInfo;
      try {
        poolInfo = await currentContract.methods.poolInfo(poolId).call();
      } catch (e) {
        console.warn('fetchProjectContractInfo: could not read poolInfo from contract:', e.message);
        return info;
      }
      // let subProjectPoolInfo;
      // if (project.subProject && project.subProject.poolId !== poolId) {
      //   subProjectPoolInfo = await currentContract.methods
      //     .poolInfo(project.subProject.poolId)
      //     .call();
      // }

      if (project.currency === "ATD") {
        tokenContract = state.oldTokenContractInfo;
      }
      if (currentAccount) {
        // info.yourStake = web3.utils.fromWei(await currentContract.methods.userStaked(poolId, currentAccount).call());
        info.yourStake = project.userProjectInfo.invested || 0;
        info.refunded = project.userProjectInfo.refunded || 0;
        if (
          !isIDO &&
          !isOTC &&
          !isOTCOnMarket &&
          !isIDOAdvanced &&
          !isTrueYieldStaking
        ) {
          // info.earned = web3.utils.fromWei(await currentContract.methods.earned(poolId, currentAccount).call());
          info.earned = project.userProjectInfo.invested;
        } else {
          if (isIDOAdvanced) {
            const {
              contractAddress: IDOCurrentAddress,
              // poolId: IDOPoolId,
              contractAbi: IDOCurrentContractAbi,
            } = project.subProject;
            const IDOCurrentContract = new web3.eth.Contract(
              IDOCurrentContractAbi,
              IDOCurrentAddress
            );

            // const yourInvested = web3.utils.fromWei(await IDOCurrentContract.methods.userStaked(IDOPoolId, currentAccount).call());
            const yourInvested = project.userProjectInfo.invested;

            info.atdBalance = web3.utils.fromWei(
              await IDOCurrentContract.methods.balanceOf(currentAccount).call()
            );

            if (tokenPrice) {
              info.earned =
                yourInvested /
                parseFloat(tokenPrice.toString().match(/\d+[\\.,]?\d+/));
            }
          } else {
            const IDOCurrentContract = new web3.eth.Contract(
              state.CPLTContractAbi,
              poolInfo.token
            );

            info.atdBalance = web3.utils.fromWei(
              await IDOCurrentContract.methods.balanceOf(currentAccount).call()
            );
            info.idoTokenAddress = poolInfo.token;

            if (tokenPrice) {
              info.earned =
                info.yourStake /
                parseFloat(tokenPrice.toString().match(/\d+[\\.,]?\d+/));
            }
          }
        }
        try {
          if (
            !isIDO &&
            !isOTC &&
            !isOTCOnMarket &&
            !isIDOAdvanced &&
            !isTrueYieldStaking
          ) {
            info.atdBalance = web3.utils.fromWei(
              await tokenContract.methods.balanceOf(currentAccount).call()
            );
          }
        } catch (e) {
          console.error(e.message);
        }
      }

      if (Number(info.earned) !== 0 && info.earned !== "--") {
        info.userParticipated = true;
      }
      // info.yourStake = 300000
      // let tokenPrices = 1.5
      // if (tokenPrices) {
      //   info.earned = info.yourStake / parseFloat(tokenPrices.toString().match(/\d+[\\.,]?\d+/));
      // }
      // info.refunded = 100000
      // info.userParticipated = true;
      return info;
    } catch (error) {
      console.error(error);
    }
  },
  async isUserAbleToClaimProject(_, id) {
    try {
      const response = await API.get("/user-able-claim", {
        params: {
          project_id: id,
        },
      });

      return response.data;
    } catch (error) {
      console.error(error);
    }
  },
  async updateInvestmentProjectPoolInfo({ dispatch, commit }, project) {
    await dispatch("initWeb3");
    await dispatch("setTokenContract");
    project.contractInfo = await dispatch("fetchProjectContractInfo", project);
    commit("UPDATE_INVESTMENT_PROJECT_CONTRACT_INFO", project);
  },
  async fetchUserInvestments({ dispatch, commit, state }) {
    commit("SET_PENDING_REQUEST_STATUS");
    let result = [];
    try {
      const response = await API.get("/investments");

      if (!state.userProfile?.userERCAddress) {
        await dispatch("fetchUserProfile");
      }

      if (response.data) {
        const userAddress = state.userProfile?.userERCAddress?.toLowerCase?.();
        const projects = Array.isArray(response.data.projects)
          ? response.data.projects
          : [];

        const mappedProjects = projects.map((project) => {
          const invested = Number(project?.userProjectInfo?.invested || 0);
          const refunded = Number(project?.userProjectInfo?.refunded || 0);
          let earned = "--";

          if (
            !project.isIDO &&
            !project.isOTC &&
            !project.isOTCOnMarket &&
            !project.isIDOAdvanced &&
            !project.isTrueYieldStaking
          ) {
            earned = invested;
          } else if (project.tokenPrice) {
            const parsedPrice = parseFloat(
              project.tokenPrice.toString().match(/\d+[\\.,]?\d+/)
            );
            if (parsedPrice) {
              earned = invested / parsedPrice;
            }
          }

          const contractInfo = {
            totalStaked: project.totalInvestments,
            yourStake: invested,
            earned,
            atdBalance: 0,
            userParticipated:
              invested > 0 ||
              (earned !== "--" && Number(earned) > 0) ||
              Boolean(project.currentUserParticipated),
            idoTokenAddress: "",
            refunded,
          };

          const normalizedProject = {
            ...project,
            contractInfo,
          };

          if (normalizedProject.showClaimButton && normalizedProject.claimProofs) {
            try {
              normalizedProject.claimProofs = JSON.parse(
                normalizedProject.claimProofs
              );
              Object.keys(normalizedProject.claimProofs).forEach((key) => {
                if (key.toLowerCase() !== key) {
                  normalizedProject.claimProofs[key.toLowerCase()] =
                    normalizedProject.claimProofs[key];
                  delete normalizedProject.claimProofs[key];
                }
              });
              normalizedProject.addressCanClaim = Boolean(
                userAddress && normalizedProject.claimProofs[userAddress]
              );
            } catch (e) {
              normalizedProject.claimProofs = null;
              normalizedProject.addressCanClaim = false;
            }
          } else {
            normalizedProject.claimProofs = null;
            normalizedProject.addressCanClaim = false;
          }

          return normalizedProject;
        });

        result = mappedProjects.map((project) => ({
          ...project,
          isUserAbleToCLaim:
            project.showClaimButton &&
            (project.isUserAbleToCLaim ??
              Boolean(project.claimProofs && project.addressCanClaim)),
        }));

        commit("SET_USER_OLD_INVESTMENTS", response.data.oldProjects);
      }
    } catch (error) {
      //alert(error);
      console.error(error);
    } finally {
      commit("SET_USER_INVESTMENTS", result);
      commit("SET_LOADED_REQUEST_STATUS");
    }
  },
  async fetchUserActivity({ commit }) {
    try {
      const response = await API.get("/user-activity");
      let sortData = response.data.sort((a, b) => new Date(b.date) - new Date(a.date))
      commit("SET_USER_ACTIVITY", sortData);
    } catch (error) {
      console.error(error);
    }
  },
  async fetchProjectsForHomePage({ commit }) {
    try {
      const response = await API.get("/projects/home-page");

      commit("SET_PROJECTS", response.data);
    } catch (error) {
      console.error(error);
    }
  },
  async fetchCompletedProjects({ commit }, page = 1) {
    try {
      const response = await API.get("/projects/completed", {
        params: {
          page: page,
        },
      });

      commit("SET_PROJECTS", { completed: response.data.data });
      commit("SET_PAGINATION_META", response.data.meta);
    } catch (error) {
      console.error(error);
    }
  },
  async fetchOTCProjects({ commit }, { page = 1, state = 'active' }) {
    try {
      const response = await API.get("/projects/otc", {
        params: {
          page: page,
          state: state
        },
      });
      commit("SET_PROJECTS_OTC_PAGE", response.data.data);
      commit("SET_PAGINATION_META", response.data.meta);
    } catch (error) {
      console.error(error);
    }
  },
  async fetchOTCOnMarketProjects({ commit }, { page = 1, state = 'active' }) {
    try {
      const response = await API.get("/projects/otcOnMarket", {
        params: {
          page: page,
          state: state
        },
      });
      commit("SET_PROJECTS_OTC_PAGE", response.data.data);
      commit("SET_PAGINATION_META", response.data.meta);
    } catch (error) {
      console.error(error);
    }
  },
  async fetchStakingProjects({ commit }, page = 1) {
    try {
      const response = await API.get("/projects/staking", {
        params: {
          page: page,
        },
      });

      commit("SET_PROJECTS", { staking: response.data.data });
      commit("SET_PAGINATION_META", response.data.meta);
    } catch (error) {
      console.error(error);
    }
  },
  async fetchWC2022Projects({ commit }, page = 1) {
    try {
      const response = await API.get("/projects/wc2022", {
        params: {
          page: page,
        },
      });

      commit("SET_PROJECTS", { wc2022: response.data.data });
      commit("SET_PAGINATION_META", response.data.meta);
    } catch (error) {
      console.error(error);
    }
  },
  async fetchI18N({ commit }) {
    try {
      const response = await API.get("/i18n/all");

      commit("SET_I18N", response.data);
    } catch (error) {
      console.error(error);
    }
  },
  async fetchSocialNetworks({ commit }) {
    try {
      const response = await API.get("/social-networks");

      commit("SET_SOCIAL_NETWORKS", response.data);
    } catch (error) {
      console.error(error);
    }
  },
  async fetchLeaderboard({ commit }) {
    try {
      const response = await API.get("/leaderboard");

      commit(SET_LEADERBOARD_DATA, response.data);
      return Number(response.data.authUser?.totalScore || 0) > 0;
    } catch (error) {
      console.error(error);
      return false;
    }
  },
  async fetchIpoLeaderboard({ commit }) {
    try {
      const response = await API.get("/ipo-leaderboard");

      commit(SET_IPO_LEADERBOARD_DATA, response.data);
    } catch (error) {
      console.error(error);
    }
  },
  // eslint-disable-next-line
  async setProjectForUser({ commit }, { projectId, stakeAmount, totalStaked }) {
    try {
      await API.post("/user/participated", {
        projectId,
        stakeAmount,
        totalStaked,
      });
    } catch (error) {
      console.error(error);
    }
  },
  changeChain({ dispatch, state }, chainId) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      await dispatch("initWeb3");
      let chainName = "BNB Smart Chain Mainnet";
      let currencySymbol = "BNB";

      if (state.provider) {
        let params = [];

        if (process.env.NODE_ENV === "development") {
          chainName = "BNB Smart Chain Testnet";
          currencySymbol = "tBNB";
        }

        if (+chainId !== +process.env.VUE_APP_CHAIN_ID) {
          params = [
            {
              chainId: process.env.VUE_APP_CHAIN_ID_HEX,
              chainName: chainName,
              nativeCurrency: {
                name: currencySymbol,
                symbol: currencySymbol,
                decimals: 18,
              },
              rpcUrls: [process.env.VUE_APP_NETWORK_URL],
              blockExplorerUrls: [process.env.VUE_APP_BLOCKEXPLORER_URL],
            },
          ];
        }

        if (params.length > 0) {
          try {
            await state.provider.request({
              method: "wallet_addEthereumChain",
              params,
            });
            resolve(true);
          } catch (e) {
            reject(e);
          }
        }

        resolve(true);
      } else {
        reject("Browser is not compatible.");
      }
    });
  },
  changeChainToETH({ dispatch, state, commit }, chainId) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      await dispatch("initWeb3");

      // let chainName = "Ethereum Mainnet";
      // let currencySymbol = "ETH";

      if (state.provider) {
        let params = [];
        commit("SET_CURRENT_NETWORK_DROPDOWN", "ETH");

        // if (process.env.NODE_ENV === "development") {
        //   chainName = "BNB Smart Chain Testnet";
        //   currencySymbol = "tBNB";
        // }

        if (+chainId !== 1) {
          params = [
            {
              chainId: "0x1",
            },
          ];
        }

        if (params.length > 0) {
          try {
            await state.provider.request({
              method: "wallet_switchEthereumChain",
              params,
            });
            resolve(true);
          } catch (e) {
            commit("SET_CURRENT_NETWORK_DROPDOWN", "BNB");

            reject(e);
          }
        }

        resolve(true);
      } else {
        reject("Browser is not compatible.");
      }
    });
  },
  changeChainToBNB({ dispatch, state, commit }, chainId) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      await dispatch("initWeb3");

      // let chainName = "Ethereum Mainnet";
      // let currencySymbol = "ETH";
      if (state.provider) {
        commit("SET_CURRENT_NETWORK_DROPDOWN", "BNB");

        let params = [];

        // if (process.env.NODE_ENV === "development") {
        //   chainName = "BNB Smart Chain Testnet";
        //   currencySymbol = "tBNB";
        // }

        if (+chainId !== 56) {
          params = [
            {
              chainId: "0x38",
            },
          ];
        }

        if (params.length > 0) {
          try {
            await state.provider.request({
              method: "wallet_switchEthereumChain",
              params,
            });
            resolve(true);
          } catch (e) {
            commit("SET_CURRENT_NETWORK_DROPDOWN", "ETH");
            reject(e);
          }
        }

        resolve(true);
      } else {
        reject("Browser is not compatible.");
      }
    });
  },
  async fetchBlogCategories({ commit }) {
    try {
      const response = await API.get("/blog-categories");
      commit("SET_BLOG_CATEGORIES", response.data);
    } catch (error) {
      console.error(error);
    }
  },
  async fetchBlogPosts({ commit }, category = null) {
    try {
      const response = await API.get("/blog-posts", {
        params: {
          category: category,
        },
      });

      commit("SET_BLOG_POSTS_BY_CATEGORY", response.data);
    } catch (error) {
      console.error(error);
    }
  },
  // eslint-disable-next-line
  async updateProjectEndTime({ commit }, slug) {
    try {
      await API.post(`/projects/${slug}/update-end-time`);
    } catch (error) {
      console.error(error);
    }
  },
  // eslint-disable-next-line
  async getBanners({ commit }) {
    try {
      const response = await API.get("/banners");

      commit("SET_BANNERS", response.data);
    } catch (error) {
      console.error(error);
    }
  },
  // eslint-disable-next-line
  async fetchBlogPostsHomePage({ commit }) {
    try {
      const response = await API.get("/blogs-homepage");

      commit("SET_BLOG_POSTS_HOME_PAGE", response.data);
    } catch (error) {
      console.error(error);
    }
  },
  async fetchIndustriesInnovationPage({ commit }) {
    try {
      const { data } = await API.get(`/innovation-industries`);
      commit("SET_INDUSTRIES_INNOVATION_PAGE", data);
    } catch (error) {
      console.error(error);
    }
  },
  async fetchProjectsInnovationPage({ commit }, slug) {
    try {
      const { data } = await API.get("/innovation-projects", {
        params: {
          industry: slug,
        },
      });
      commit("SET_PROJECTS_INNOVATION_PAGE", data);
    } catch (error) {
      console.error(error);
    }
  },
  async fetchPartners({ commit }) {
    try {
      const response = await API.get("/partners");

      commit("SET_PARTNERS_DATA", response.data);
    } catch (error) {
      console.error(error);
    }
  },
  // eslint-disable-next-line
  async setExpressInterest({ commit }, payload) {
    try {
      await API.post(`/user/set-interest`, payload);
    } catch (error) {
      console.error(error);
    }
  },
  async getReadMethods({ state, commit }, network) {
    let web3 = state.web3;
    const currentAccount = state.userProfile.userERCAddress;
    let contract;

    if (network === "ETH") {
      contract = new web3.eth.Contract(ATD_TOKEN_ABI, etherium.atdToken);
    } else if (network === "BNB") {
      contract = new web3.eth.Contract(ATD_TOKEN_ABI, binance.atdToken);
    }
    const balanceOf = await contract.methods.balanceOf(currentAccount).call();
    const symbol = await contract.methods.symbol().call();
    const decimals = await contract.methods.decimals().call();
    commit("SET_ATD_TOKEN_DATA", {
      balance: balanceOf,
      symbol: symbol,
      decimals: decimals,
    });
  },
  async getAtdToken() {
    throw new Error("ATD burning is disabled");
  },
  async stakeAtdToken() {
    throw new Error("ATD staking is disabled");
  },
  async likeProject({ state, commit }, projectId) {
    try {
      const { data } = await API.post("/innovation-labs/like", {
        project_id: projectId,
      });

      commit("SET_USER_PROFILE", {
        ...state.userProfile,
        projectLikes: [...state.userProfile.projectLikes, projectId],
      });
      commit("SET_СURRENT_LIKES_FOR_PROJECT", {
        likes: data.likes,
        projectId: projectId,
      });
    } catch (e) {
      console.log(e);
    }
  },
  async dislikeProject({ state, commit }, projectId) {
    try {
      const { data } = await API.post("/innovation-labs/like", {
        project_id: projectId,
      });

      commit("SET_USER_PROFILE", {
        ...state.userProfile,
        projectLikes: state.userProfile.projectLikes.filter(
          (value) => value !== projectId
        ),
      });
      commit("SET_СURRENT_LIKES_FOR_PROJECT", {
        likes: data.likes,
        projectId: projectId,
      });
    } catch (e) {
      console.log(e);
    }
  },
  async interestProject({ state, commit }, projectId) {
    try {
      const { data } = await API.post("/innovation-labs/interest", {
        project_id: projectId,
      });

      commit("SET_USER_PROFILE", {
        ...state.userProfile,
        projectInterests: [...state.userProfile.projectInterests, projectId],
      });
      commit("SET_СURRENT_INTERESTS_FOR_PROJECT", {
        interests: data.interests,
        projectId: projectId,
      });
    } catch (e) {
      console.log(e);
    }
  },
  async uninterestProject({ state, commit }, projectId) {
    try {
      const { data } = await API.post("/innovation-labs/interest", {
        project_id: projectId,
      });

      commit("SET_USER_PROFILE", {
        ...state.userProfile,
        projectInterests: state.userProfile.projectInterests.filter(
          (value) => value !== projectId
        ),
      });
      commit("SET_СURRENT_INTERESTS_FOR_PROJECT", {
        interests: data.interests,
        projectId: projectId,
      });
    } catch (e) {
      console.log(e);
    }
  },
  async getReadMethodsStake({ state, commit }, network) {
    let web3 = state.web3;
    const currentAccount = state.userProfile.userERCAddress;
    let contract;

    if (network === "ETH") {
      contract = new web3.eth.Contract(STAKING_ABI, etherium.stakingAddress);
    } else if (network === "BNB") {
      contract = new web3.eth.Contract(STAKING_ABI, binance.stakingAddress);
    }
    let userStakes = {}
    for (let i = 1; i <= 3; i++) {
      const userStakesAndPenalties = await contract.methods.userStakesAndPenalties(currentAccount, i).call();
      userStakes[i] = {
        penalties: userStakesAndPenalties.penalties,
        stakeAmounts: userStakesAndPenalties.stakeAmounts,
        stakeStarts: userStakesAndPenalties.stakeStarts,
        period: (12 / i) === 4 ? 3 : 12 / i,
        poolId: i,
      }
    }
    commit(SET_USER_STAKES, userStakes)

  },
  async unStakeAtdToken(
    { state, commit },
    { poolId, stakeId, amount, network }
  ) {
    // console.log(poolId, stakeId, amount, network)
    let web3 = state.web3;
    // let success = false;
    let amountContract = web3.utils.toWei(amount.toString());


    const currentAccount = state.userProfile.userERCAddress;
    let contract;
    //
    if (network === "ETH") {
      contract = new web3.eth.Contract(STAKING_ABI, etherium.stakingAddress);
    } else if (network === "BNB") {
      contract = new web3.eth.Contract(STAKING_ABI, binance.stakingAddress);
    }

    try {
      const gasLimit = await contract.methods
        .unstakeExactStake(poolId.toString(), stakeId.toString(), amountContract)
        .estimateGas({ from: currentAccount });
      const gasPrice = await web3.eth.getGasPrice();
      await contract.methods
        .unstakeExactStake(poolId.toString(), stakeId.toString(), amountContract)
        .send({
          from: currentAccount,
          gasLimit: Math.round(gasLimit * 1.1),
          gasPrice: gasPrice,
        });
      commit("UNSTAKE_LOADING", false);
      // success = true;
    } catch (e) {
      commit("UNSTAKE_LOADING", false);

      console.log(e);
    }
  },
};



export default actions;
